/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import RelatorioDetalhesModal from './RelatorioDetalhesModal';
import OcorrenciasRelatoriosTab from './OcorrenciasRelatoriosTab';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  TrendingUp,
  CalendarMonth,
  People,
  WbSunny,
  LightMode,
  NightsStay,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays, startOfMonth, endOfMonth } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';

export default function ConsultaRelatoriosModal({ open, onClose }) {
  const { hasPermission, isAdmin, token } = useAuth();
  const canUpdate = hasPermission('/relatorio-obras', 'update');
  const canDelete = hasPermission('/relatorio-obras', 'delete');
  const [relatorios, setRelatorios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [metricas, setMetricas] = useState({
    totalRDOs: 0,
    rdosUltimos30: 0,
    rdosMesAtual: 0,
    clientesAtivos: 0,
    rdosManha: 0,
    rdosTarde: 0,
    rdosNoite: 0,
  });
  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [selectedRelatorioId, setSelectedRelatorioId] = useState(null);
  const [detalhesStartEdit, setDetalhesStartEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const API = '/api';

  const handleView = (id) => {
    setDetalhesStartEdit(false);
    setSelectedRelatorioId(id);
    setDetalhesOpen(true);
  };

  const handleEdit = (id) => {
    // Abrir detalhes já em modo edição (sem sair da tela)
    setDetalhesStartEdit(true);
    setSelectedRelatorioId(id);
    setDetalhesOpen(true);
  };

  // Helper para obter nome do cliente
  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId || c.id === Number(clienteId));
    return cliente?.nome || `Cliente ID: ${clienteId}`;
  };

  const handleDelete = async (id) => {
    if (!id || deletingId) return;

    try {
      setDeletingId(id);
      setError('');

      const authToken = token || localStorage.getItem('token');
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const endpoints = [`${API}/relatorios_obras/${id}`, `${API}/relatorios_obras/${id}/`];

      let deleted = false;
      let lastMessage = '';

      for (const endpoint of endpoints) {
        const res = await fetch(endpoint, {
          method: 'DELETE',
          headers,
        });

        if (res.ok) {
          deleted = true;
          break;
        }

        let detail = '';
        try {
          const data = await res.json();
          detail = data?.detail || data?.error || '';
        } catch {
          detail = '';
        }

        lastMessage = detail || `Falha ao excluir relatório (HTTP ${res.status})`;

        // Se não encontrou na rota sem barra, tenta a rota com barra.
        if (res.status === 404) continue;

        break;
      }

      if (!deleted) {
        throw new Error(lastMessage || 'Não foi possível excluir o relatório.');
      }

      setDeleteConfirm(null);
      await fetchData();
    } catch (e) {
      console.error('Erro ao excluir:', e);
      setError(e?.message || 'Erro ao excluir relatório');
    } finally {
      setDeletingId(null);
    }
  };

  const sanitizeFilenamePart = (value) => {
    if (!value) return 'sem-valor';
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  const formatDateForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-data';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatTimeForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-horario';
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${hh}-${min}`;
  };

  const handleDownloadRelatorio = async (rel) => {
    try {
      const token = localStorage.getItem('token');
      let relatorioCompleto = rel;

      try {
        const res = await fetch(`${API}/relatorios_obras/${rel.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) relatorioCompleto = await res.json();
      } catch (fetchErr) {
        console.warn('Usando dados da listagem para gerar PDF:', fetchErr);
      }

      const clienteDisplay = getClienteNome(relatorioCompleto.cliente_id);
      const clienteNome = sanitizeFilenamePart(clienteDisplay);
      const dataParte = formatDateForFilename(relatorioCompleto.data_relatorio || relatorioCompleto.created_at);
      const horaParte = formatTimeForFilename(relatorioCompleto.created_at || relatorioCompleto.data_relatorio);
      const numeroChamado = sanitizeFilenamePart(
        relatorioCompleto.numero_chamado || relatorioCompleto.numero_relatorio || `rdo-${relatorioCompleto.id}`
      );
      const filename = `${clienteNome}_${dataParte}_${horaParte}_${numeroChamado}.pdf`;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pageW - margin * 2;
      const black = [60, 60, 60];
      const border = [120, 120, 120];
      let y = 12;

      const tempoDisplay =
        relatorioCompleto.tempo === 'manha'
          ? 'Manhã'
          : relatorioCompleto.tempo === 'tarde'
            ? 'Tarde'
            : relatorioCompleto.tempo === 'noite'
              ? 'Noite'
              : relatorioCompleto.tempo || 'N/A';

      const diaSemana = (() => {
        try {
          const d = parseISO(relatorioCompleto.data_relatorio);
          return format(d, 'EEEE', { locale: ptBR }).replace(/^./, (c) => c.toUpperCase());
        } catch {
          return 'N/A';
        }
      })();

      const maoDeObra = parseArray(relatorioCompleto.mao_de_obra);
      const equipamentos = parseArray(relatorioCompleto.equipamentos);
      const atividades = parseArray(relatorioCompleto.atividades);
      const fotosUrls = relatorioCompleto.fotos_urls || [];
      const videosUrls = relatorioCompleto.videos_urls || [];
      const docsUrls = relatorioCompleto.documentos_urls || [];

      const textoItem = (item) => {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return String(item || '-');
        return item.descricao || item.nome || item.atividade || item.equipamento || JSON.stringify(item);
      };

      const totalFuncionarios = maoDeObra.reduce((acc, item) => {
        if (item && typeof item === 'object') return acc + (parseInt(item.funcionarios, 10) || 0);
        return acc + 1;
      }, 0);

      const loadImageAsDataUrl = async (url) => {
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error(`Falha ao carregar imagem ${url}`);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };

      const getImageSize = (dataUrl) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => resolve({ width: 1, height: 1 });
        img.src = dataUrl;
      });

      const addCell = (x, cellY, w, h, text, options = {}) => {
        const { bold = false, align = 'left', fill = null, fontSize = 7, valign = 'middle' } = options;
        pdf.setDrawColor(...border);
        if (fill) {
          pdf.setFillColor(...fill);
          pdf.rect(x, cellY, w, h, 'FD');
        } else {
          pdf.rect(x, cellY, w, h);
        }
        pdf.setTextColor(...black);
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(String(text ?? ''), w - 3);
        const textH = lines.length * (fontSize * 0.36 + 0.8);
        let ty = valign === 'top' ? cellY + 4 : cellY + (h - textH) / 2 + 3;
        const tx = align === 'center' ? x + w / 2 : align === 'right' ? x + w - 2 : x + 2;
        pdf.text(lines, tx, ty, { align });
      };

      const ensureSpace = (height) => {
        if (y + height > pageH - 28) {
          pdf.addPage();
          y = 12;
        }
      };

      const addSectionTitle = (title) => {
        ensureSpace(8);
        addCell(margin, y, contentW, 6, title, { bold: true, fill: [235, 235, 235], fontSize: 7 });
        y += 6;
      };

      const addFooter = () => {
        const pages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pages; i += 1) {
          pdf.setPage(i);
          pdf.setFontSize(7);
          pdf.text(`${i} / ${pages}`, pageW - 17, pageH - 8, { align: 'right' });
        }
      };

      pdf.setTextColor(...black);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.text(`Relatório ${formatData(relatorioCompleto.data_relatorio)} n° ${relatorioCompleto.id}`, margin, y);
      pdf.setFillColor(255, 153, 0);
      pdf.rect(pageW - 48, y - 3.5, 38, 5, 'F');
      y += 6;

      const headerY = y;
      addCell(margin, headerY, contentW - 50, 25, '', {});
      try {
        const logo = await loadImageAsDataUrl('/logo-mercadao.svg');
        // Renderiza o SVG como imagem no PDF — usa bounding box maior para manter proporções
        pdf.addImage(logo, 'SVG', margin + 2, headerY + 6, (contentW - 50) - 4, 12);
      } catch {
        // Fallback com texto estilizado
        pdf.setFontSize(10);
        pdf.setTextColor(...black);
        pdf.setFont('helvetica', 'bold');
        pdf.text('MERCADÃO ATACADISTA', margin + (contentW - 50) / 2, headerY + 13, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
      }
      addCell(margin + contentW - 50, headerY, 25, 5, 'Relatório n°', { bold: true });
      addCell(margin + contentW - 25, headerY, 25, 5, relatorioCompleto.id, { bold: true, align: 'center' });
      addCell(margin + contentW - 50, headerY + 5, 25, 5, 'Data do relatório', { bold: true });
      addCell(margin + contentW - 25, headerY + 5, 25, 5, formatData(relatorioCompleto.data_relatorio), { align: 'center' });
      addCell(margin + contentW - 50, headerY + 10, 25, 5, 'Dia da semana', { bold: true });
      addCell(margin + contentW - 25, headerY + 10, 25, 5, diaSemana, { align: 'center' });
      addCell(margin + contentW - 50, headerY + 15, 25, 5, 'Contrato', { bold: true });
      addCell(margin + contentW - 25, headerY + 15, 25, 5, relatorioCompleto.contrato_numero || 'N/A', { align: 'center' });
      addCell(margin + contentW - 50, headerY + 20, 25, 5, 'Horário envio', { bold: true });
      addCell(margin + contentW - 25, headerY + 20, 25, 5, formatCreatedAt(relatorioCompleto.created_at) || 'N/A', { align: 'center' });
      y += 25;

      addCell(margin, y, contentW, 6, 'Relatório Diário de Obra (RDO)', { bold: true, align: 'center', fill: [235, 235, 235], fontSize: 8 });
      y += 6;
      addCell(margin, y, 28, 5, 'Obra', { bold: true });
      addCell(margin + 28, y, contentW - 28, 5, relatorioCompleto.obra_nome || clienteDisplay || 'N/A');
      y += 5;
      addCell(margin, y, 28, 5, 'Local', { bold: true });
      addCell(margin + 28, y, contentW - 28, 5, relatorioCompleto.endereco || 'N/A');
      y += 5;
      addCell(margin, y, 28, 5, 'Contratada', { bold: true });
      addCell(margin + 28, y, 70, 5, relatorioCompleto.contratada || 'RANJ');
      addCell(margin + 98, y, 28, 5, 'Responsável', { bold: true });
      addCell(margin + 126, y, contentW - 126, 5, relatorioCompleto.responsavel || 'MERCADÃO');
      y += 7;

      addSectionTitle('Condição climática');
      addCell(margin, y, contentW / 3, 5, 'Turno', { bold: true });
      addCell(margin + contentW / 3, y, contentW / 3, 5, 'Tempo', { bold: true });
      addCell(margin + (contentW / 3) * 2, y, contentW / 3, 5, 'Condição', { bold: true });
      y += 5;
      addCell(margin, y, contentW / 3, 5, tempoDisplay);
      addCell(margin + contentW / 3, y, contentW / 3, 5, relatorioCompleto.condicao || 'N/A');
      addCell(margin + (contentW / 3) * 2, y, contentW / 3, 5, relatorioCompleto.condicao_execucao || 'Praticável');
      y += 7;

      addSectionTitle(`Mão de obra (total: ${totalFuncionarios})`);
      if (maoDeObra.length > 0) {
        maoDeObra.forEach((item) => {
          ensureSpace(5);
          const nome = typeof item === 'object' ? item.nome || 'Empresa' : String(item);
          const qtd = typeof item === 'object' ? item.funcionarios || 0 : 1;
          addCell(margin, y, contentW - 25, 5, nome);
          addCell(margin + contentW - 25, y, 25, 5, String(qtd), { align: 'center' });
          y += 5;
        });
      } else {
        addCell(margin, y, contentW, 5, 'Nenhuma mão de obra registrada');
        y += 5;
      }
      y += 2;

      addSectionTitle(`Equipamentos (${equipamentos.length})`);
      if (equipamentos.length > 0) {
        equipamentos.forEach((item) => {
          ensureSpace(5);
          addCell(margin, y, contentW, 5, textoItem(item));
          y += 5;
        });
      } else {
        addCell(margin, y, contentW, 5, 'Nenhum equipamento registrado');
        y += 5;
      }
      y += 2;

      addSectionTitle(`Atividades (${atividades.length})`);
      if (atividades.length > 0) {
        atividades.forEach((item) => {
          const desc = textoItem(item);
          const lines = pdf.splitTextToSize(desc, contentW - 33);
          const rowH = Math.max(5, lines.length * 3.5 + 2);
          ensureSpace(rowH);
          addCell(margin, y, contentW - 32, rowH, desc, { valign: 'top' });
          addCell(margin + contentW - 32, y, 32, rowH, item?.status || 'Em Andamento', { align: 'center', fontSize: 6 });
          y += rowH;
        });
      } else {
        addCell(margin, y, contentW, 5, 'Nenhuma atividade registrada');
        y += 5;
      }
      y += 2;

      addSectionTitle('Ocorrências');
      const ocorrencias = relatorioCompleto.descricao_servico || relatorioCompleto.transcricao_audio || 'Nenhuma ocorrência registrada.';
      const ocorrLines = pdf.splitTextToSize(ocorrencias, contentW - 4);
      const ocorrH = Math.max(8, ocorrLines.length * 3.5 + 4);
      ensureSpace(ocorrH);
      addCell(margin, y, contentW, ocorrH, ocorrencias, { valign: 'top' });
      y += ocorrH + 4;

      if (relatorioCompleto.latitude && relatorioCompleto.longitude) {
        addSectionTitle('Localização');
        addCell(margin, y, contentW, 6, `${relatorioCompleto.latitude}, ${relatorioCompleto.longitude}`);
        y += 8;
      }

      if (fotosUrls.length > 0) {
        addSectionTitle(`Fotos (${fotosUrls.length})`);
        const gap = 8;
        const imgW = (contentW - gap) / 2;
        const imgH = 78;
        for (let i = 0; i < fotosUrls.length; i += 2) {
          ensureSpace(imgH + 3);
          for (let col = 0; col < 2; col += 1) {
            const url = fotosUrls[i + col];
            if (!url) continue;
            const x = margin + col * (imgW + gap);
            pdf.setDrawColor(...border);
            pdf.rect(x, y, imgW, imgH);
            try {
              const dataUrl = await loadImageAsDataUrl(url);
              const size = await getImageSize(dataUrl);
              const ratio = Math.min((imgW - 4) / size.width, (imgH - 4) / size.height);
              const drawW = size.width * ratio;
              const drawH = size.height * ratio;
              pdf.addImage(dataUrl, 'JPEG', x + (imgW - drawW) / 2, y + (imgH - drawH) / 2, drawW, drawH);
            } catch {
              pdf.setFontSize(7);
              pdf.text(`Foto ${i + col + 1} indisponível`, x + imgW / 2, y + imgH / 2, { align: 'center' });
            }
          }
          y += imgH + 4;
        }
      }

      if (videosUrls.length > 0 || docsUrls.length > 0) {
        ensureSpace(18);
        addSectionTitle('Anexos');
        [...videosUrls.map((url, idx) => `Vídeo ${idx + 1}: ${url}`), ...docsUrls.map((url, idx) => `Documento ${idx + 1}: ${url}`)].forEach((item) => {
          const lines = pdf.splitTextToSize(item, contentW - 4);
          const rowH = Math.max(5, lines.length * 3.5 + 2);
          ensureSpace(rowH);
          addCell(margin, y, contentW, rowH, item, { valign: 'top', fontSize: 6 });
          y += rowH;
        });
      }

      ensureSpace(18);
      pdf.setDrawColor(...black);
      pdf.line(margin + 18, y + 10, margin + 68, y + 10);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(5);
      pdf.text('Assinatura', margin + 43, y + 13, { align: 'center' });
      y += 18;

      addFooter();
      pdf.save(filename);
    } catch (e) {
      console.error('Erro ao baixar relatório:', e);
      setError('Erro ao baixar relatório');
    }
  };

  const handleCloseDetalhes = () => {
    setDetalhesOpen(false);
    setSelectedRelatorioId(null);
    setDetalhesStartEdit(false);
  };

  const fetchData = useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Verificar quais clientes o usuário tem acesso
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }

      const [relatoriosRes, clientesRes] = await Promise.all([
        fetch(`${API}/relatorios_obras/`, { headers }),
        fetch(`${API}/clientes/`, { headers }),
      ]);

      if (!relatoriosRes.ok || !clientesRes.ok) {
        throw new Error('Erro ao buscar dados');
      }

      let relatoriosData = await relatoriosRes.json();
      let clientesData = await clientesRes.json();

      // Filtrar se houver restrição de clientes
      if (clienteIds && clienteIds.length > 0) {
        const idsNum = clienteIds.map(Number);
        clientesData = clientesData.filter(c => idsNum.includes(Number(c.id)));
        relatoriosData = relatoriosData.filter(r => idsNum.includes(Number(r.cliente_id)));
      }

      setRelatorios(relatoriosData);
      setClientes(clientesData);
      setResultados(relatoriosData);

      calcularMetricas(relatoriosData);
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API, open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calcularMetricas = (relatorios) => {
    const hoje = new Date();
    const ultimos30 = subDays(hoje, 30);
    const mesInicio = startOfMonth(hoje);
    const mesFim = endOfMonth(hoje);

    const rdosUltimos30 = relatorios.filter((r) => new Date(r.data_relatorio) >= ultimos30).length;

    const rdosMes = relatorios.filter((r) => {
      const data = new Date(r.data_relatorio);
      return data >= mesInicio && data <= mesFim;
    }).length;

    const clientesSet = new Set(relatorios.map((r) => r.cliente_id).filter(Boolean));

    const rdosPorPeriodo = relatorios.reduce(
      (acc, r) => {
        const periodo = r.periodo?.toLowerCase() || '';
        if (periodo === 'manhã' || periodo === 'manha') acc.manha++;
        else if (periodo === 'tarde') acc.tarde++;
        else if (periodo === 'noite') acc.noite++;
        return acc;
      },
      { manha: 0, tarde: 0, noite: 0 }
    );

    setMetricas({
      totalRDOs: relatorios.length,
      rdosUltimos30,
      rdosMesAtual: rdosMes,
      clientesAtivos: clientesSet.size,
      rdosManha: rdosPorPeriodo.manha,
      rdosTarde: rdosPorPeriodo.tarde,
      rdosNoite: rdosPorPeriodo.noite,
    });
  };

  const handleBuscar = () => {
    let filtered = [...relatorios];

    if (filtroCliente) {
      filtered = filtered.filter((r) => r.cliente_id === parseInt(filtroCliente));
    }

    if (dataInicio) {
      filtered = filtered.filter((r) => new Date(r.data_relatorio) >= new Date(dataInicio));
    }

    if (dataFim) {
      filtered = filtered.filter((r) => new Date(r.data_relatorio) <= new Date(dataFim));
    }

    setResultados(filtered);
  };

  const handleLimpar = () => {
    setFiltroCliente('');
    setDataInicio('');
    setDataFim('');
    setResultados(relatorios);
  };

  const formatData = (dataStr) => {
    try {
      return format(parseISO(dataStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dataStr;
    }
  };

  const formatCreatedAt = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getPeriodoColor = (periodo) => {
    const p = periodo?.toLowerCase() || '';
    if (p === 'manhã' || p === 'manha') return 'warning';
    if (p === 'tarde') return 'info';
    if (p === 'noite') return 'secondary';
    return 'default';
  };

  const metricasCards = [
    { title: 'Total de RDOs', value: metricas.totalRDOs, icon: TrendingUp, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { title: 'RDOs Mês Atual', value: metricas.rdosMesAtual, icon: CalendarMonth, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { title: 'Últimos 30 Dias', value: metricas.rdosUltimos30, icon: TrendingUp, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { title: 'Clientes Ativos', value: metricas.clientesAtivos, icon: People, gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
    { title: 'RDOs Manhã', value: metricas.rdosManha, icon: WbSunny, gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
    { title: 'RDOs Tarde', value: metricas.rdosTarde, icon: LightMode, gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' },
    { title: 'RDOs Noite', value: metricas.rdosNoite, icon: NightsStay, gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2,
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          📊 Consulta de Relatórios de Obras
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabs principais - Busca e Ocorrências (admin only) */}
        {isAdmin && isAdmin() && (
          <Tabs
            value={mainTabIndex}
            onChange={(e, v) => setMainTabIndex(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Buscar Relatórios" icon={<SearchIcon />} iconPosition="start" />
            <Tab label="Ocorrências" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        )}

        {/* Tab 0: Busca normal */}
        {mainTabIndex === 0 && (
          <>
            {/* Métricas */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                📈 Dashboard de Métricas
              </Typography>
              <Grid container spacing={2}>
                {metricasCards.map((metrica, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card
                      sx={{
                        background: metrica.gradient,
                        color: 'white',
                        height: '100%',
                        minHeight: 120,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <metrica.icon sx={{ fontSize: 32, opacity: 0.9 }} />
                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {metrica.value}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.95 }}>
                          {metrica.title}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Filtros */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                🔍 Filtros de Busca
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 260 } }}>
                  <TextField
                    select
                    fullWidth
                    label="Cliente"
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {clientes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Data Início"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Data Fim"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1 180px', minWidth: { xs: '100%', sm: 180 } }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleBuscar}
                      fullWidth
                      size="small"
                    >
                      Buscar
                    </Button>
                  </Box>
                </Box>
              </Box>
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={handleLimpar}>
              Limpar Filtros
            </Button>
          </Box>
        </Box>

        {/* Resultados */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            📋 Resultados ({resultados.length})
          </Typography>
          
          {loading ? (
            <Typography>Carregando...</Typography>
          ) : resultados.length === 0 ? (
            <Alert severity="info">Nenhum relatório encontrado.</Alert>
          ) : (
            <Grid container spacing={2}>
              {resultados.map((rel) => (
                <Grid item xs={12} sm={6} md={4} key={rel.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {rel.numero_relatorio || `RDO #${rel.id}`}
                        </Typography>
                        <Chip
                          label={rel.periodo || 'N/A'}
                          color={getPeriodoColor(rel.periodo)}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        📅 {formatData(rel.data_relatorio)}
                      </Typography>
                      
                      {rel.cliente_id && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          👤 {getClienteNome(rel.cliente_id)}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        ⏰ {rel.tempo === 'manha' ? 'Manhã' : rel.tempo === 'tarde' ? 'Tarde' : rel.tempo === 'noite' ? 'Noite' : rel.tempo || 'N/A'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        ☁️ Condição climática: {rel.condicao || 'N/A'}
                      </Typography>

                      {(() => {
                        const maoDeObra = parseArray(rel.mao_de_obra);
                        const equipamentos = parseArray(rel.equipamentos);
                        const atividades = parseArray(rel.atividades);
                        
                        // Calcular total de funcionários
                        const totalFuncionarios = maoDeObra.reduce((acc, e) => {
                          if (typeof e === 'object' && e.funcionarios) {
                            return acc + (parseInt(e.funcionarios) || 0);
                          }
                          return acc + 1; // Para formato antigo (array simples)
                        }, 0);
                        
                        // Verificar se é formato novo (com objetos)
                        const formatoNovo = maoDeObra.length > 0 && typeof maoDeObra[0] === 'object' && maoDeObra[0].nome;
                        
                        return (
                          <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              <Chip 
                                size="small" 
                                variant="outlined" 
                                color={totalFuncionarios > 0 ? 'primary' : 'default'}
                                label={`Mão de obra: ${totalFuncionarios}`} 
                              />
                              <Chip size="small" variant="outlined" label={`Equipamentos: ${equipamentos.length}`} />
                              <Chip size="small" variant="outlined" label={`Atividades: ${atividades.length}`} />
                            </Box>
                            {formatoNovo && maoDeObra.length > 0 && (
                              <Box sx={{ mt: 1, pl: 1 }}>
                                {maoDeObra.filter(e => e.funcionarios > 0).map((empresa, idx) => (
                                  <Typography key={idx} variant="caption" display="block" color="text.secondary">
                                    • {empresa.nome}: {empresa.funcionarios}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </>
                        );
                      })()}

                      {rel.latitude && rel.longitude ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            📍 Resultado (localização)
                          </Typography>
                          <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                            <iframe
                              title={`Mapa RDO ${rel.id}`}
                              width="100%"
                              height="160"
                              style={{ border: 0 }}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(rel.longitude) - 0.005}%2C${Number(rel.latitude) - 0.005}%2C${Number(rel.longitude) + 0.005}%2C${Number(rel.latitude) + 0.005}&layer=mapnik&marker=${Number(rel.latitude)}%2C${Number(rel.longitude)}`}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📍 Resultado: localização não registrada
                        </Typography>
                      )}

                      {rel.created_at && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          🕐 Enviado: {formatCreatedAt(rel.created_at)}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadRelatorio(rel)}
                          title="Baixar PDF"
                          sx={{ color: '#d32f2f' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => handleView(rel.id)} title="Ver detalhes">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {canUpdate && (
                          <IconButton size="small" color="secondary" onClick={() => handleEdit(rel.id)} title="Editar">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(rel.id)} title="Excluir">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
          </>
        )}

        {/* Tab 1: Ocorrências (admin only) */}
        {mainTabIndex === 1 && isAdmin && isAdmin() && (
          <OcorrenciasRelatoriosTab token={token} />
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>

      <RelatorioDetalhesModal
        open={detalhesOpen}
        onClose={handleCloseDetalhes}
        relatorioId={selectedRelatorioId}
        clientes={clientes}
        startEdit={detalhesStartEdit}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={!!deletingId}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(deleteConfirm)}
            disabled={deletingId === deleteConfirm}
          >
            {deletingId === deleteConfirm ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
